import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { useNxSocket } from "@blpde/connect";
import {
  buildAuthHeaders,
  getApiOrigin,
  handleAuthFailure,
  hasAuthCredentials,
  tryRefreshAccessToken,
} from "@/auth";

// Master's global mention register (GET/POST /v1/mentions…): one row per
// "you were mentioned on resource X in module Y", across every module. The
// polymorphic reference (moduleKey + resourceType/resourceID) plus the
// pre-rendered resourceLabel/link mean the page never needs module lookups.
export interface MasterMention {
  id: number;
  uuid?: string;
  moduleID?: number;
  moduleKey?: string;
  actorAccountID?: number;
  resourceType?: string;
  resourceID?: string;
  resourceLabel?: string;
  contextID?: string;
  excerpt?: string;
  link?: string;
  data?: unknown;
  readAt?: string | null;
  createdAt?: string;
}

export interface MentionListQuery {
  unread?: boolean;
  moduleID?: number;
  beforeID?: number;
  limit?: number;
}

interface ListResponse {
  mentions?: MasterMention[];
  unreadCount?: number;
}

// Shared badge state: the nav bell and the page both read this; every list
// fetch refreshes it.
const unreadCount = ref(0);

function authHeaders(): Headers {
  return buildAuthHeaders({ Accept: "application/json" });
}

async function request(
  path: string,
  init: RequestInit = {},
  canRefresh = true,
): Promise<Response> {
  const headers = authHeaders();
  for (const [key, value] of new Headers(init.headers).entries()) {
    headers.set(key, value);
  }
  const res = await fetch(`${getApiOrigin()}/v1${path}`, { ...init, headers });
  if ((res.status === 401 || res.status === 403) && canRefresh) {
    const refreshed = await tryRefreshAccessToken();
    if (refreshed) return request(path, init, false);
    handleAuthFailure();
  }
  if (!res.ok) throw new Error(`mentions request failed (${res.status})`);
  return res;
}

export async function fetchMentions(query: MentionListQuery = {}): Promise<{
  mentions: MasterMention[];
  unreadCount: number;
}> {
  const params = new URLSearchParams();
  if (query.unread) params.set("unread", "1");
  if (query.moduleID) params.set("moduleID", String(query.moduleID));
  if (query.beforeID) params.set("beforeID", String(query.beforeID));
  params.set("limit", String(query.limit ?? 50));
  const res = await request(`/mentions?${params.toString()}`);
  const data = (await res.json()) as ListResponse;
  const count = Number(data.unreadCount ?? 0);
  unreadCount.value = count;
  return { mentions: data.mentions ?? [], unreadCount: count };
}

export async function fetchMentionUnreadCount(): Promise<number> {
  const res = await request("/mentions/unread-count");
  const data = (await res.json()) as { count?: number };
  const count = Number(data.count ?? 0);
  unreadCount.value = count;
  return count;
}

export async function markMentionRead(id: number): Promise<void> {
  await request(`/mentions/${id}/read`, { method: "POST" });
  unreadCount.value = Math.max(0, unreadCount.value - 1);
}

export async function markAllMentionsRead(): Promise<void> {
  await request("/mentions/read-all", { method: "POST" });
  unreadCount.value = 0;
}

/** master's cap on the ids of one bulk request. */
const MAX_BULK_IDS = 200;

function chunks(ids: number[]): number[][] {
  const out: number[][] = [];
  for (let i = 0; i < ids.length; i += MAX_BULK_IDS)
    out.push(ids.slice(i, i + MAX_BULK_IDS));
  return out;
}

async function postIDs(
  path: string,
  ids: number[],
  field: "marked" | "deleted",
): Promise<number> {
  let total = 0;
  for (const chunk of chunks(ids)) {
    const res = await request(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mentionIDs: chunk }),
    });
    total += Number(
      ((await res.json()) as Record<string, number | undefined>)[field] ?? 0,
    );
  }
  return total;
}

/** Marks a read mention unread again. */
export async function markMentionUnread(id: number): Promise<void> {
  const res = await request(`/mentions/${id}/unread`, { method: "POST" });
  const data = (await res.json()) as { marked?: number };
  if (Number(data.marked ?? 0) > 0) unreadCount.value += 1;
}

/** Marks several mentions read, in requests of up to 200 ids; returns how many changed. */
export async function markMentionsRead(ids: number[]): Promise<number> {
  if (!ids.length) return 0;
  const marked = await postIDs("/mentions/read", ids, "marked");
  unreadCount.value = Math.max(0, unreadCount.value - marked);
  return marked;
}

/**
 * Deletes the caller's mentions by id; master deletes each mention's
 * notification with it. Returns how many mentions went.
 */
export async function deleteMentions(ids: number[]): Promise<number> {
  if (!ids.length) return 0;
  let deleted = 0;
  if (ids.length === 1) {
    const res = await request(`/mentions/${ids[0]}`, { method: "DELETE" });
    deleted = Number(((await res.json()) as { deleted?: number }).deleted ?? 0);
  } else {
    deleted = await postIDs("/mentions/delete", ids, "deleted");
  }
  void fetchMentionUnreadCount().catch(() => undefined);
  return deleted;
}

/** Deletes every read mention of the caller (and their notifications); unread ones stay. */
export async function deleteReadMentions(): Promise<number> {
  const res = await request("/mentions/delete-read", { method: "POST" });
  const data = (await res.json()) as { deleted?: number };
  return Number(data.deleted ?? 0);
}

// Badge composable for the shell nav. Mentions have no dedicated push lane —
// every mention also creates a notification, so the master channel's
// notification.* frames double as the mention wake-up signal. A deleted
// mention takes its notification along, so `notification.delete` counts too:
// a tab that did not delete it still drops the badge.
export function useMentionUnread() {
  const socket = useNxSocket();
  const unsubs: Array<() => void> = [];

  onMounted(() => {
    if (hasAuthCredentials())
      void fetchMentionUnreadCount().catch(() => undefined);
    const channel = socket?.channel("master");
    if (channel) {
      unsubs.push(
        channel.on("event", (env) => {
          if (
            env?.evt === "notification.new" ||
            env?.evt === "notification.update" ||
            env?.evt === "notification.delete"
          ) {
            void fetchMentionUnreadCount().catch(() => undefined);
          }
        }),
      );
    }
  });

  onBeforeUnmount(() => {
    while (unsubs.length) unsubs.pop()?.();
  });

  return { unreadCount: computed(() => unreadCount.value) };
}
